import { useEffect, useRef, useState } from 'react';

/**
 * Zone de tracé de la signature manuscrite. Dessine en coordonnées CSS mais
 * dimensionne le canvas au devicePixelRatio pour un tracé net sur écran HiDPI.
 *
 * La résolution interne du canvas est recalée à chaque changement de taille
 * (ResizeObserver) : mesurer une seule fois au montage désynchronise le
 * tracé du pointeur dès que la mise en page bouge après coup (chargement du
 * devis, redimensionnement de la fenêtre) — le tracé finit décalé par
 * rapport au curseur, de plus en plus vers les bords.
 */
function SignaturePad({ onValider, validation }) {
  const canvasRef = useRef(null);
  const dessinEnCours = useRef(false);
  const [vide, setVide] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    function ajusterTaille() {
      const ratio = window.devicePixelRatio || 1;
      const { width, height } = canvas.getBoundingClientRect();
      if (!width || !height) return;

      canvas.width = width * ratio;
      canvas.height = height * ratio;

      // setTransform (absolu) plutôt que scale (relatif) : ce recalage peut
      // s'exécuter plusieurs fois, scale() se serait cumulé à chaque appel.
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#19181c';
    }

    ajusterTaille();

    const observer = new ResizeObserver(ajusterTaille);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  function position(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function demarrer(e) {
    canvasRef.current.setPointerCapture(e.pointerId);
    const { x, y } = position(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    dessinEnCours.current = true;
  }

  function tracer(e) {
    if (!dessinEnCours.current) return;
    const { x, y } = position(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setVide(false);
  }

  function arreter() {
    dessinEnCours.current = false;
  }

  function effacer() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // clearRect ignore les coordonnées CSS : le contexte est mis à l'échelle
    // (setTransform ci-dessus), donc effacer en (canvas.width, canvas.height)
    // sans revenir à l'identité ne nettoierait qu'un coin du canevas.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setVide(true);
  }

  function valider() {
    onValider(canvasRef.current.toDataURL('image/png'));
  }

  return (
    <div className="signature-pad">
      <canvas
        ref={canvasRef}
        className="signature-pad-canvas"
        onPointerDown={demarrer}
        onPointerMove={tracer}
        onPointerUp={arreter}
        onPointerLeave={arreter}
      />
      <div className="signature-pad-actions">
        <button type="button" className="devis-btn" onClick={effacer} disabled={vide || validation}>
          Effacer
        </button>
        <button
          type="button"
          className="devis-btn devis-btn-primary"
          onClick={valider}
          disabled={vide || validation}
        >
          {validation ? 'Enregistrement' : 'Valider la signature'}
        </button>
      </div>
    </div>
  );
}

export default SignaturePad;
